'use client';

import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Input from '@/components/atoms/input';
import PasswordInput from '@/components/atoms/password-input';
import { Account } from '@shared/types/account/Account';
import { useTranslations } from 'use-intl';
import useCustomRouter from '@/hooks/useCustomRouter';
import useSwrClearCache from '@/hooks/useSwrClearCache';
import { LoginProps } from './types';
import { sdk } from '@/sdk/CommercetoolsSDK';
import Button from '@/components/atoms/button';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { useRouter } from 'next/navigation';


const Login: FC<LoginProps> = ({ login, requestPasswordReset, ...props }) => {
  const handlePwdPaste = () => { };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue('email', e.target.value);
    if (error) setError(undefined);
    if (emptyFieldsError) setEmptyFieldsError(false);
    if (userdoesntexists) setUserdoesntexists(false);
    if (networkError) setNetworkError('');
  };

  const router = useRouter();
  const handleBlur = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  const handlePaste = () => { };
  const handleKeyDown = () => { };
  const clearCache = useSwrClearCache();
  const translate = useTranslations();
  const extensions = sdk.composableCommerce;

  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState<'signIn' | 'register' | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm<Account & { rememberMe?: boolean; confirmPassword?: string }>({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
      confirmPassword: '',
    },
  });

  const data = watch();
  const [resetting, setResetting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState<string>();
  const [inputError, setInputError] = useState<string>();
  const [emptyFieldsError, setEmptyFieldsError] = useState(false);
  const [validCustomer, setValidCustomer] = useState(false);
  const [networkError, setNetworkError] = useState('');
  const [userdoesntexists, setUserdoesntexists] = useState(false);
  const [resetRequested, setResetRequested] = useState(false);
  const [registerRequested, setRegisterRequested] = useState(false);

  const goBackToLogin = () => {
    setRequested(false);
    setResetting(false);
    setError(undefined);
    setInputError(undefined);
    setEmptyFieldsError(false);
    setUserdoesntexists(false);
  };

  const gotToReset = () => {
    setResetting(true);
    reset();
    setError(undefined);
    setInputError(undefined);
    setEmptyFieldsError(false);
    setUserdoesntexists(false);
  };

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (error) setError(undefined);
    if (emptyFieldsError) setEmptyFieldsError(false);
    if (userdoesntexists) setUserdoesntexists(false);
    if (networkError) setNetworkError('');

    const { name, value, checked } = target;
    setValue(name as keyof Account, name === 'rememberMe' ? checked : value);
  };

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const verify = urlSearchParams.get('success');
    const supportSignUp = urlSearchParams.get('supportSignUp');

    if (verify === 'false') {
      toast.success("You're all set! Log in below to access your account", { duration: 5000 });
    } else if (verify === 'true') {
      let messageKey = supportSignUp === 'true'
        ? 'Thank you for verifying, please log in below.'
        : 'Your password has been reset, please log in below.';
      toast.success(messageKey, { duration: 5000 });
    }
  }, []);

  const handleLoginSubmit = async () => {
    try {
      setNetworkError('');
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!data.email || !emailRegex.test(data.email) || !data.password) {
        setEmptyFieldsError(true);
        setError('Please enter a valid email address and password.');
        return;
      }

      setEmptyFieldsError(false);
      setUserdoesntexists(false);
      setLoginLoading(true);

      const response = await login(data?.email?.toLowerCase(), data.password, data.rememberMe);

      if (response?.loginResStatusCode === 200) {
        if (response.onboarding_workflow_status !== 'MemberRegistered') {
          router.push('/create-profile');
          router.refresh();
          clearCache();
        } else if (response.onboarding_workflow_status === 'MemberRegistered') {
          router.push('/');
        }
      }
    } catch (err) {
      let errMessae = ''
      if (err instanceof Error) errMessae = err.message;
      setNetworkError('');
      setUserdoesntexists(false);
      setEmptyFieldsError(false);
      setError(undefined);

      if (errMessae == 'Error: UserDoesntExistsInCT') {
        setUserdoesntexists(true);
      } else if (errMessae == 'Error: Please verify your email before logging in.') {
        setError('Please verify your email address before logging in.  Check your inbox for the verification email and click the link to activate your account.');
        setEmptyFieldsError(true);
      } else if (errMessae == 'Error: Wrong email or password.') {
        setError('Please enter a valid email address and password.');
        setEmptyFieldsError(true);
      } else {
        setNetworkError("We're having trouble completing your request. Please try again later");
      }
      setLoginLoading(false);
    }
  };

  const handleResetSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.email || !emailRegex.test(data.email)) {
      setEmptyFieldsError(true);
      setError('Please enter a valid email address.');
      return;
    }

    const email = data.email.toLowerCase();
    const requestBody = { email: email };
    setLoading(true);

    try {
      const responseBody = await extensions.account.handleResetSubmit(JSON.stringify(requestBody));
      if (responseBody?.data?.data?.Response === 'Not a valid Customer') {
        setValidCustomer(true)
        setLoading(false);
      } else if (responseBody?.data?.data?.StatusCode === 200) {
        setResetRequested(true);
        setLoading(false);
      } else {
        setLoading(false);
        setError(translate('error.email'));
      }
    } catch (error) {
      setLoading(false);
      console.error('Reset Password Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async () => {
    const capitalRegex = /[A-Z]/;
    const smallRegex = /[a-z]/;
    const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;
    const numberRegex = /[0-9]/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const registerData = {
      email: data.email,
      password: data.password,
      confirmPassword: data.confirmPassword,
    };

    if (!registerData.email || !registerData.password) {
      setEmptyFieldsError(true);
      setError("Please enter a valid email address or password.");
      return;
    }

    if (!emailRegex.test(registerData.email)) {
      setError("Please enter a valid email address.");
      setEmptyFieldsError(true);
      return;
    }

    const passwordRegexPassed = [
      smallRegex.test(registerData.password),
      capitalRegex.test(registerData.password),
      specialCharRegex.test(registerData.password),
      numberRegex.test(registerData.password)
    ].filter(Boolean).length;

    if (
      !registerData.password ||
      registerData.password.length < 8 ||
      passwordRegexPassed < 3
    ) {
      setEmptyFieldsError(true);
      setError("Password must be at least 8 characters long and include at least three of the following: uppercase letters, lowercase letters, numbers, or special characters.");
      return;
    }
    /*
        const confirmPasswordRegexPassed = [
          smallRegex.test(registerData.confirmPassword),
          capitalRegex.test(registerData.confirmPassword),
          specialCharRegex.test(registerData.confirmPassword),
          numberRegex.test(registerData.confirmPassword)
        ].filter(Boolean).length;
    
    
        if (
          !registerData.confirmPassword ||
          registerData.confirmPassword.length < 8 ||
          confirmPasswordRegexPassed < 3
        ) {
          setEmptyFieldsError(true);
          setError("Password must be at least 8 characters long and contain at least one capital letter, one special character and one number.");
          return;
        }
    */
    if (registerData.confirmPassword !== registerData.password) {
      setError("Your passwords don't match. Please ensure both password fields are identical.");
      setEmptyFieldsError(true);
      return;
    }

    setRegisterLoading(true);

    try {
      const encodedPassword = Buffer.from(registerData.password).toString('base64');
      const email = registerData.email.toLowerCase();
      const requestBody = {
        Username: email,
        Password: encodedPassword,
        ConfirmPassword: encodedPassword,
      };

      const responseBody = await extensions.account.handleRegisterSubmit(JSON.stringify(requestBody));

      if (responseBody?.data?.data?.StatusCode === 200) {
        setRegisterRequested(true);
        setError(undefined);
      } else if (
        responseBody?.data?.data?.StatusCode === 400 &&
        responseBody.data.data.Value.ErrorMessage === 'The user already exists.'
      ) {
        setError("There is already an existing customer with the provided email.");
      } else if (
        responseBody?.data?.data?.StatusCode === 400 &&
        responseBody.data.data.Value.ErrorMessage === 'UnableToValidateCustomerInD365'
      ) {
        setError("Unable to validate customer. Please check your details.");
      } else {
        setError("Registration failed. Please try again.");
      }
    } catch (error) {
      console.error('Register Error:', error);
      setError("An error occurred during registration.");
    } finally {
      setRegisterLoading(false);
    }
  };

  // Block background scroll when sidebar is open
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    if (sidebarOpen) {
      const scrollY = window.scrollY;
      const body = document.body;

      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.overflow = 'hidden';

      body.dataset.scrollY = scrollY.toString();

      return () => {
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.overflow = '';

        const savedScrollY = parseInt(body.dataset.scrollY || '0', 10);
        window.scrollTo(0, savedScrollY);

        delete body.dataset.scrollY;
      };
    }

    return undefined;
  }, [sidebarOpen]);
  const handleResendVerification = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.email || !emailRegex.test(data.email)) {
      setEmptyFieldsError(true);
      setError('Please enter a valid email address.');
      return;
    }

    const email = data.email.toLowerCase();
    const requestBody = { Email: email };

    try {
      setLoading(true);

      const response = await extensions.account.sendVerification(JSON.stringify(requestBody));

      if (response?.data?.data?.StatusCode === 200) {
        toast.success('Verification link sent', {
          position: 'bottom-right',
          duration: 5000,
        });

        const endTime = Date.now() + 120 * 1000; // 120 seconds
        setCooldownEnd(endTime);
      } else {
        toast.success('Failed to send verification link.', {
          position: 'bottom-right',
          duration: 5000,
        });
        setError('Failed to send verification link.');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  };
  const [cooldown, setCooldown] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState(null);

  useEffect(() => {
    setCooldown(0);
    setCooldownEnd(null);
  }, [data.email, data.password]);

  useEffect(() => {
    if (!cooldownEnd) return;

    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((cooldownEnd - Date.now()) / 1000));
      setCooldown(remaining);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownEnd]);


  return (
    <div className="w-full flex justify-center items-center bg-[#f8f3f0] py-12">
      {/* Desktop View */}
      <div className="max-w-4xl hidden lg:flex w-full flex-col md:flex-row justify-center items-center px-4">
        {/* Left side - Sign In */}
        <div className="hidden lg:flex flex-col items-center text-center md:pr-20 pr-0 border-r border-gray-300">
          <h2 className="text-40 leading-[60px] font-medium text-[#000000]">Welcome back</h2>
          <p className="text-20 leading-[25px] font-normal text-[#000000]">
            Click here to log back into your account
          </p>
          <button
            className="bg-black text-white w-full mt-5 cursor-pointer text-25 leading-[60px] font-medium max-w-[360px] rounded whitespace-nowrap shadow hover:bg-gray-800 transition"
            onClick={() => {
              setSidebarOpen('signIn');
              reset();
              setResetting(false);
              setError(undefined);
              setInputError(undefined);
              setEmptyFieldsError(false);
              setUserdoesntexists(false);
            }}
          >
            Sign In
          </button>
        </div>


        {/* Right side - Register */}
        <div className="flex flex-col items-center text-center md:pl-20 pl-0">
          <h2 className="text-40 leading-[60px] font-medium text-[#000000]">First time here?</h2>
          <p className="text-20 leading-[25px] font-normal text-[#000000]">
            Are you new to Caroma Plumbers&apos; Hub?
          </p>
          <button
            className="bg-black text-white w-full mt-5 text-25 cursor-pointer leading-[60px] font-medium max-w-[360px] whitespace-nowrap rounded shadow hover:bg-gray-800 transition"
            onClick={() => {
              setSidebarOpen('register');
              reset();
              setResetting(false);
              setError(undefined);
              setInputError(undefined);
              setEmptyFieldsError(false);
              setRegisterRequested(false);
              setUserdoesntexists(false);
            }}
          >
            Click here to Register
          </button>
        </div>
      </div>
      {/* MOBILE VIEW */}
      <div className="w-full max-w-xs flex lg:hidden flex-col gap-4">
        {/* Welcome back section */}
        <div className="flex flex-col items-center text-center">
          <h2 className="text-32 leading-[48px] font-medium mb-2">Welcome back</h2>
          <p className="text-14 leading-[20px] font-normal mb-4">Click here to log back into your account</p>
          <button
            className="w-full bg-black mt-2 text-white py-3 rounded shadow hover:bg-gray-800 transition font-semibold text-base mb-2"
            onClick={() => {
              setSidebarOpen('signIn');
              reset();
              setResetting(false);
              setError(undefined);
              setInputError(undefined);
              setEmptyFieldsError(false);
              setUserdoesntexists(false);
            }}
          >
            Sign In
          </button>
        </div>
        <hr className="my-1 border-gray-300 px-5" />
        {/* First time here section */}
        <div className="flex flex-col items-center text-center">
          <h2 className="text-32 leading-[48px] font-medium mb-2">First time here?</h2>
          <p className="text-14 leading-[20px] font-normal mb-4">Are you new to Caroma Plumbers&apos; Hub?</p>
          <button
            className="w-full bg-black mt-2 text-white py-3 rounded shadow hover:bg-gray-800 transition font-semibold text-base"
            onClick={() => {
              setSidebarOpen('register');
              reset();
              setResetting(false);
              setError(undefined);
              setInputError(undefined);
              setEmptyFieldsError(false);
              setRegisterRequested(false);
              setUserdoesntexists(false);
            }}
          >
            Click here to Register
          </button>
        </div>
      </div>


      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity duration-300 ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        aria-hidden="true"
        onClick={() => {
          if (sidebarOpen) {
            setSidebarOpen(null);
            reset();
            setResetting(false);
            setError(undefined);
            setInputError(undefined);
            setEmptyFieldsError(false);
            setResetRequested(false);
            setUserdoesntexists(false);
          }
        }}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-[100dvh] w-full lg:w-[30%] bg-white shadow-lg flex flex-col items-center justify-start lg:justify-center overflow-y-auto overscroll-contain z-50 transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{ willChange: 'transform' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          className="absolute top-[calc(1rem+env(safe-area-inset-top))] right-4 cursor-pointer text-gray-500 w-[35px] h-[35px] flex items-center justify-center"
          onClick={() => {
            setSidebarOpen(null);
            reset();
            setResetting(false);
            setError(undefined);
            setInputError(undefined);
            setEmptyFieldsError(false);
            setResetRequested(false);
            setUserdoesntexists(false);
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Sign In Form */}
        {sidebarOpen === 'signIn' && !resetting && (
          <form className="w-full px-8 pt-[max(1.5rem,env(safe-area-inset-top))] pb-10 lg:pt-0 lg:pb-0 text-center max-w-[570px]" onSubmit={handleSubmit(handleLoginSubmit)}>
            <h2 className="text-32 leading-[38px] font-medium mb-2">Sign in for</h2>
            <h2 className="text-32 leading-[38px] font-medium mb-6">Caroma Plumbers&apos; Hub</h2>

            {emptyFieldsError && (
        <div className="mb-4 text-red-600 font-medium">
          {error}

          {error?.includes("Please verify your email address") && (
            <div className="mt-2">
              <button
                onClick={handleResendVerification}
                disabled={cooldown > 0}
                className={`${
                  cooldown > 0
                    ? "text-black underline cursor-not-allowed text-14 text-[#494949]"
                    : "text-black text-14 underline"
                }`}
              >
                {cooldown > 0
                  ? `Resend in ${Math.floor(cooldown / 60)}:${String(
                      cooldown % 60
                    ).padStart(2, "0")}`
                  : "Resend Verification Link"}
              </button>
            </div>
          )}
        </div>
      )}

            {userdoesntexists && (
              <span className="mb-4 text-red-600 font-medium">
                We're having trouble logging you in. Please{' '}
                <Link href="/contact-us" className="!underline">contact us</Link>{' '}
                for support.
              </span>
            )}

            {networkError && (
              <span className="mb-4 text-red-600 font-medium">{networkError}</span>
            )}

            <Input
              containerClassName={`w-full !rounded-lg mb-5 ${emptyFieldsError ? '!border-red-500' : ''}`}
              className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
              name="email"
              placeholder={"Your Email Address"}
              value={data.email ?? ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              aria-label="email"
              tabIndex={0}
            />

            <PasswordInput
              containerClassName={`w-full !rounded-lg mt-3 mb-5 sm:mb-4 ${emptyFieldsError ? '!border-red-500' : ''}`}
              className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
              name="password"
              placeholder={translate('account.password')}
              value={data.password ?? ''}
              onChange={(e) => {
                setValue('password', e.target.value);
                if (error) setError(undefined);
                if (emptyFieldsError) setEmptyFieldsError(false);
                setUserdoesntexists(false);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePwdPaste}
              onBlur={handleBlur}
              tabIndex={0}
              aria-label="password"
            />

            <div className="mb-4 text-center text-14 leading-[22px] font-normal text-[#090909]">
              <span>Forgot your password? </span>
              <button type="button" className="text-black underline cursor-pointer" onClick={gotToReset}>
                Reset here
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-black !py-5 text-white cursor-pointer rounded mb-2 font-normal text-14 disabled:!bg-gray-400"
              variant={'ghost'}
              disabled={!!error}
            >
              {loginLoading && (
                <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                  <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                </svg>
              )}
              {loginLoading ? "Signing In" : "Sign In"}
            </Button>

            <div className="mt-5 text-14 leading-[22px] font-normal text-[#494949]">
              First time here?{' '}
              <button
                type="button"
                className="underline text-14 leading-[22px] cursor-pointer font-normal text-[#494949]"
                onClick={() => {
                  setSidebarOpen('register');
                  reset();
                  setResetting(false);
                  setError(undefined);
                  setInputError(undefined);
                  setEmptyFieldsError(false);
                  setRegisterRequested(false);
                  setUserdoesntexists(false);
                }}
              >
                Create your account
              </button>
            </div>
          </form>
        )}

        {/* Reset Password Form */}
        {sidebarOpen === 'signIn' && resetting && (
          <form className="w-full px-8 pt-[max(1.5rem,env(safe-area-inset-top))] pb-10 lg:pt-0 lg:pb-0 text-center max-w-[570px]" onSubmit={handleSubmit(handleResetSubmit)}>
            <h2 className="text-32 leading-[38px] font-medium mb-6">Reset password</h2>

            {resetRequested ? (
              <>
                <div className="mt-8 w-full self-stretch font-normal text-14 text-black max-md:max-w-full">
                  We have sent an email to <span className="font-semibold">{data?.email}</span> with a password reset instructions.
                </div>
                <Button
                  variant={'ghost'}
                  onClick={() => {
                    goBackToLogin();
                    reset();
                    setError(undefined);
                    setInputError(undefined);
                    setEmptyFieldsError(false);
                    setResetRequested(false);
                    setUserdoesntexists(false);
                  }}
                  className="mt-8 items-stretch justify-center cursor-pointer whitespace-nowrap bg-gray-800 px-7 py-3.5 text-14 font-normal leading-6 text-white shadow-sm max-md:px-5"
                >
                  Return to Sign In
                </Button>
              </>
            ) : (
              <>
                <p className="mb-6 text-20 leading-[25px] font-normal">
                  Enter the email address associated with your account
                </p>

                {emptyFieldsError && (
                  <div className="mt-4 text-red-600 font-medium">
                    Please enter a valid email address.
                  </div>
                )}
                {validCustomer && (
                  <div className="mt-4 text-red-600 font-medium">
                    Email not found.
                  </div>
                )}

                <Input
                  containerClassName={`w-full !rounded-lg mt-5 mb-5 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  name="email"
                  placeholder={"Your Email Address"}
                  value={data.email ?? ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  aria-label="email"
                  tabIndex={0}
                  autoComplete="email"
                />

                <Button
                  type="submit"
                  className="w-full bg-black py-5 text-white rounded mb-2 font-normal text-14 cursor-pointer disabled:!bg-gray-400"
                  disabled={!!error}
                  variant={'ghost'}
                >
                  {loading && (
                    <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                    </svg>
                  )}
                  {loading ? "Getting reset link" : "Get reset link"}
                </Button>

                <div className="mt-2 text-center text-14">
                  First time here?{' '}
                  <button
                    type="button"
                    className="text-black underline cursor-pointer"
                    onClick={() => {
                      setSidebarOpen('register');
                      setResetting(false);
                      reset();
                      setError(undefined);
                      setInputError(undefined);
                      setEmptyFieldsError(false);
                      setRegisterRequested(false);
                      setUserdoesntexists(false);
                    }}
                  >
                    Create your account
                  </button>
                </div>

                <div className="mt-2 text-center text-14">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-black underline cursor-pointer"
                    onClick={() => {
                      goBackToLogin();
                      reset();
                      setError(undefined);
                      setInputError(undefined);
                      setEmptyFieldsError(false);
                      setUserdoesntexists(false);
                    }}
                  >
                    Sign in
                  </button>
                </div>
              </>
            )}
          </form>
        )}

        {/* Register Form */}
        {sidebarOpen === 'register' && (
          <form className="w-full px-8 pt-[max(1.5rem,env(safe-area-inset-top))] pb-10 lg:pt-0 lg:pb-0 text-center max-w-[570px]" onSubmit={handleSubmit(handleRegisterSubmit)}>
            {registerRequested ? (
              <div className="flex min-h-screen items-center justify-center bg-white px-4">
                <div className="max-w-md w-full text-center">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-black"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>

                    <h1 className="text-32 font-medium mb-4">
                      Account registration confirmed
                    </h1>
                  </div>

                  <p className="text-gray-700 mb-8 text-14 font-normal">

                    A verification email has been sent to <span className="font-semibold">{data.email}</span>.
                    Please verify your account to gain access and log in.
                  </p>

                  <button
                    className="w-full bg-black mt-5 cursor-pointer text-white py-3 font-normal text-14 hover:bg-gray-800 transition"
                    onClick={() => {
                      setSidebarOpen("signIn");
                      reset();
                      setResetting(false);
                      setError(undefined);
                      setInputError(undefined);
                      setEmptyFieldsError(false);
                      setUserdoesntexists(false);
                    }}
                  >
                    Log in
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-32 leading-[38px] font-medium mb-6">
                  Register for<br />Caroma Plumbers&apos; Hub
                </h2>

                {error && (
                  <div className="mb-4 text-red-600 font-medium">{error}</div>
                )}

                <Input
                  containerClassName={`w-full !rounded-lg mb-5 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  name="email"
                  placeholder={"Your Email Address"}
                  value={data.email ?? ''}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  aria-label="email"
                  tabIndex={0}
                />

                <PasswordInput
                  containerClassName={`w-full !rounded-lg mt-3 mb-5 sm:mb-4 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  name="password"
                  placeholder={translate('account.password')}
                  value={data.password ?? ''}
                  onChange={(e) => {
                    setValue('password', e.target.value);
                    if (error) setError(undefined);
                    if (emptyFieldsError) setEmptyFieldsError(false);
                    setUserdoesntexists(false);
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePwdPaste}
                  onBlur={handleBlur}
                  tabIndex={0}
                  aria-label="password"
                />

                <PasswordInput
                  containerClassName={`w-full !rounded-lg mt-3 mb-5 sm:mb-4 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  className={`w-full py-3 ${emptyFieldsError ? '!border-red-500' : ''}`}
                  name="password"
                  placeholder={"Confirm Password"}
                  value={data.confirmPassword ?? ''}
                  onChange={(e) => {
                    setValue('confirmPassword', e.target.value);
                    if (error) setError(undefined);
                    if (emptyFieldsError) setEmptyFieldsError(false);
                    setUserdoesntexists(false);
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePwdPaste}
                  onBlur={handleBlur}
                  tabIndex={0}
                  aria-label="password"
                />

                <Button
                  variant={'ghost'}
                  type="submit"
                  className="w-full bg-black text-white py-5 rounded mb-4 font-normal text-14 cursor-pointer disabled:!bg-gray-400"
                  disabled={!!error}
                >
                  {registerLoading && (
                    <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                    </svg>
                  )}
                  {registerLoading ? "Activating" : "Continue"}
                </Button>

                <div className="mt-2 text-14">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-black underline cursor-pointer"
                    onClick={() => {
                      setSidebarOpen('signIn');
                      reset();
                      setResetting(false);
                      setError(undefined);
                      setInputError(undefined);
                      setEmptyFieldsError(false);
                      setUserdoesntexists(false);
                    }}
                  >
                    Sign in
                  </button>
                </div>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
